import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import { 
    BarChart3, Wallet, TrendingUp, Users, Link2, History, Plus, DollarSign,
    ArrowUpRight, ArrowDownRight, Filter, Calendar, ChevronRight, Search,
    Layers, Target, X, Download, TrendingDown, Layout
} from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useDevice } from '../hooks/useDevice';
import { useSettings } from '../context/SettingsContext';

const AnalyticCard = ({ label, value, icon: Icon, color, isLarge = false }) => (
    <div className={`bg-neutral-900 border border-neutral-800 ${isLarge ? 'p-8 md:p-10 rounded-[2.5rem] md:rounded-[3.5rem]' : 'p-6 md:p-8 rounded-[2rem] md:rounded-[3rem]'} shadow-2xl relative overflow-hidden group italic transition-all hover:border-blue-500/20`}>
        <div className={`absolute top-0 right-0 p-8 opacity-5 -mr-8 -mt-8 group-hover:scale-110 transition-transform duration-700 text-${color}-500`}>
            {Icon && React.cloneElement(Icon, { size: isLarge ? 120 : 80 })}
        </div>
        <p className="text-[9px] md:text-[10px] font-black text-neutral-500 uppercase tracking-widest mb-2 md:mb-3">{label}</p>
        <p className={`${isLarge ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'} font-black text-white tracking-tighter leading-none`}>
            {value}
        </p>
    </div>
);

const Analytics = () => {
    const { user } = useAuth();
    const { isMobile } = useDevice();
    const { currency } = useSettings();
    const queryClient = useQueryClient();
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    
    const [activeTab, setActiveTab] = useState('overview');
    const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
    const [newPayout, setNewPayout] = useState({ owner_id: '', amount: '', notes: '' });

    // Queries
    const { data: stats = {}, isLoading: isStatsLoading } = useQuery({ queryKey: ['partner-stats'], queryFn: async () => (await client.get('/referrals/stats')).data });
    const { data: payouts = [], isLoading: isPayoutsLoading } = useQuery({ queryKey: ['partner-payouts'], queryFn: async () => (await client.get('/referrals/payouts')).data });
    const { data: usersData = [] } = useQuery({ queryKey: ['users-partners'], queryFn: async () => (await client.get('/users')).data, enabled: isAdmin });
    const { data: sourceStats = [], isLoading: isSourceLoading } = useQuery({ queryKey: ['lead-source-stats'], queryFn: async () => (await client.get('/crm/analytics/sources')).data, enabled: isAdmin });

    const payoutMutation = useMutation({
        mutationFn: (data) => client.post('/referrals/payouts', data),
        onSuccess: () => {
            queryClient.invalidateQueries(['partner-payouts']); queryClient.invalidateQueries(['partner-stats']);
            setIsPayoutModalOpen(false); setNewPayout({ owner_id: '', amount: '', notes: '' });
            toast.success('Выплата зарегистрирована');
        }
    });

    const handleCreatePayout = (e) => { e.preventDefault(); payoutMutation.mutate(newPayout); };

    if (isStatsLoading) return (
        <div className="flex items-center justify-center min-h-[400px]">
             <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    // --- RESPONSIVE UI ---
    return (
        <div className="max-w-7xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700 pb-24 md:pb-32 px-4 italic">
            {/* Unified Header */}
            <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-6 pt-4 md:pt-8">
                <div>
                    <h1 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">Аналитика</h1>
                    <p className="text-neutral-500 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] md:tracking-[0.4em] mt-2 md:mt-3 italic">Venture Insight Platform</p>
                </div>
                {isAdmin && (
                    <button 
                        onClick={() => setIsPayoutModalOpen(true)} 
                        className="w-full md:w-auto bg-white text-black px-8 md:px-10 py-5 rounded-2xl md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest hover:bg-neutral-200 transition-all shadow-2xl flex items-center justify-center gap-3 italic"
                    >
                        <Plus size={20}/> {isMobile ? 'Выплата' : 'Сделать выплату'}
                    </button>
                )}
            </header>

            {/* Metrics Row - Responsive Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                <AnalyticCard label="Текущий Баланс" value={`${stats.balance} ${currency}`} icon={<Wallet/>} color="emerald" isLarge={!isMobile} />
                <AnalyticCard label="Заработано всего" value={`${stats.earned} ${currency}`} icon={<TrendingUp/>} color="blue" isLarge={!isMobile} />
                <AnalyticCard label="Выплачено" value={`${stats.paid} ${currency}`} icon={<DollarSign/>} color="purple" isLarge={!isMobile} />
                <AnalyticCard label="Конверсия" value={`${stats.clicks > 0 ? ((stats.leads / stats.clicks) * 100).toFixed(1) : 0}%`} icon={<Target/>} color="amber" isLarge={!isMobile} />
            </div>

            {/* Analytics Content - Charts & Channels */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Efficiency Chart */}
                <div className="lg:col-span-2 bg-neutral-900 border border-neutral-800 p-6 md:p-10 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl relative overflow-hidden group">
                    <div className="flex justify-between items-center mb-8 md:mb-10">
                        <div>
                             <h3 className="text-lg md:text-2xl font-black text-white italic uppercase tracking-tighter leading-none mb-2">Эффективность Канала</h3>
                             <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-widest">Динамика трафика в реальном времени</p>
                        </div>
                        <div className="flex gap-2">
                            <div className="w-2 md:w-3 h-2 md:h-3 rounded-full bg-blue-600 shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
                            <div className="w-2 md:w-3 h-2 md:h-3 rounded-full bg-emerald-600 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                        </div>
                    </div>
                    
                    <div className="h-[250px] md:h-[400px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={getMockData(stats.clicks)}>
                                <defs>
                                    <linearGradient id="cBlue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#262626" />
                                <XAxis dataKey="name" stroke="#525252" fontSize={10} fontStyle="italic" hide={isMobile} />
                                <Tooltip contentStyle={{backgroundColor: '#0a0a0a', border: '1px solid #262626', borderRadius: '1.5rem'}} />
                                <Area type="monotone" dataKey="clicks" stroke="#3b82f6" strokeWidth={isMobile ? 2 : 4} fill="url(#cBlue)" />
                                <Area type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={isMobile ? 2 : 4} fill="transparent" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Stats Footer (Visible on all screens) */}
                    <div className="flex justify-around mt-8 md:mt-10 pt-6 md:pt-8 border-t border-white/5 italic font-black">
                        <div className="text-center"><p className="text-[8px] md:text-[10px] text-neutral-600 uppercase tracking-widest mb-1">Клики</p><p className="text-lg md:text-2xl text-white">{stats.clicks}</p></div>
                        <div className="text-center"><p className="text-[8px] md:text-[10px] text-neutral-600 uppercase tracking-widest mb-1">Лиды</p><p className="text-lg md:text-2xl text-emerald-500">{stats.leads}</p></div>
                        <div className="text-center"><p className="text-[8px] md:text-[10px] text-neutral-600 uppercase tracking-widest mb-1">CR %</p><p className="text-lg md:text-2xl text-blue-500">{stats.clicks > 0 ? ((stats.leads / stats.clicks) * 100).toFixed(1) : 0}</p></div>
                    </div>
                </div>

                {/* Secondary Section - Channels or Payouts depending on user */}
                <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-10 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl relative overflow-hidden">
                    <h3 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter mb-8 flex items-center gap-4">
                        <Link2 className="text-blue-500"/> Активные Каналы
                    </h3>
                    <div className="space-y-4 md:space-y-6">
                        <div className="p-6 md:p-8 bg-neutral-950 border border-neutral-800 rounded-3xl md:rounded-[2rem] hover:border-blue-500/30 transition-all group">
                            <h4 className="text-base md:text-xl font-black text-white italic uppercase mb-1">Main Channel</h4>
                            <p className="text-[9px] md:text-[10px] text-neutral-500 font-bold uppercase tracking-widest mb-6 italic truncate">@million_connections_bot</p>
                            <div className="flex justify-between pt-4 border-t border-white/5">
                                <div><p className="text-[8px] md:text-[9px] text-neutral-600 font-black uppercase italic">Клики</p><p className="text-lg md:text-xl font-black text-white italic">{stats.clicks}</p></div>
                                <div className="text-right"><p className="text-[8px] md:text-[9px] text-neutral-600 font-black uppercase italic">Конверсии</p><p className="text-lg md:text-xl font-black text-emerald-500 italic">{stats.leads}</p></div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Recent Payouts Preview (Only show on desktop or if it was there) */}
                    <div className="mt-10">
                        <h4 className="text-[10px] font-black text-neutral-500 uppercase tracking-widest mb-4 italic">Последние начисления</h4>
                        <div className="space-y-3">
                             {stats.earned > 0 ? (
                                <div className="p-4 bg-neutral-950 rounded-2xl border border-neutral-800 flex justify-between items-center group active:scale-[0.98] transition-all">
                                    <div>
                                        <p className="text-white font-black uppercase text-[10px] italic">Тариф Клубный</p>
                                        <p className="text-[8px] text-neutral-500 italic uppercase">Commission #001</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-emerald-500 font-black italic text-sm">+{stats.earned} {currency}</p>
                                        <p className="text-[8px] text-neutral-700 italic">Today</p>
                                    </div>
                                </div>
                             ) : <p className="text-center py-6 text-neutral-800 text-[9px] font-black uppercase tracking-widest">Нет транзакций</p>}
                        </div>
                    </div>
                </div>
            </div>

            {/* Payout Modal */}
            {isPayoutModalOpen && (
                <div className="fixed inset-0 bg-black/95 backdrop-blur-3xl z-[150] flex items-center justify-center p-4">
                    <div className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-[3rem] md:rounded-[4rem] p-8 md:p-12 shadow-2xl flex flex-col relative overflow-hidden italic transition-all animate-in zoom-in-95 duration-300">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-2xl md:text-3xl font-black text-white uppercase italic tracking-tighter">Оформить Выплату</h3>
                            <button onClick={() => setIsPayoutModalOpen(false)} className="w-10 h-10 md:w-14 md:h-14 bg-neutral-800 rounded-xl md:rounded-2xl flex items-center justify-center hover:bg-white hover:text-black transition-all">
                                <X size={24}/>
                            </button>
                        </div>
                        <form onSubmit={handleCreatePayout} className="space-y-6 md:space-y-8">
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Получатель</label>
                                <select required value={newPayout.owner_id} onChange={e => setNewPayout({...newPayout, owner_id: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 md:p-6 rounded-2xl md:rounded-[2rem] font-bold outline-none appearance-none">
                                    <option value="">-- Выбрать партнера --</option>
                                    {usersData.filter(u => u.role !== 'admin').map(u => <option key={u.id} value={u.id}>{u.name || u.fio} (ID: {u.id})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Сумма вознаграждения</label>
                                <div className="relative">
                                    <input required type="number" value={newPayout.amount} onChange={e => setNewPayout({...newPayout, amount: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 text-white p-6 rounded-2xl md:rounded-[2rem] font-black text-3xl md:text-4xl italic text-blue-500 outline-none" placeholder="0.00" />
                                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-700 font-black italic">{currency}</span>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest px-2 italic">Заметка</label>
                                <textarea value={newPayout.notes} onChange={e => setNewPayout({...newPayout, notes: e.target.value})} className="w-full bg-neutral-950 border border-neutral-800 text-white p-5 md:p-6 rounded-2xl md:rounded-[2rem] font-bold outline-none h-24 md:h-32 resize-none" placeholder="Комментарий к транзакции..." />
                            </div>
                            <button type="submit" disabled={payoutMutation.isPending} className="w-full bg-blue-600 text-white py-6 md:py-8 rounded-[2rem] md:rounded-[2.5rem] font-black uppercase text-[10px] md:text-xs tracking-widest shadow-2xl active:scale-95 transition-all mt-4 italic shadow-blue-600/30">
                                {payoutMutation.isPending ? 'Обработка...' : 'Подтвердить начисление'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const getMockData = (clicks) => {
    const base = clicks > 0 ? (clicks / 30) : 50;
    return Array.from({length: 30}).map((_, i) => ({
        name: `${i+1} Apr`,
        clicks: Math.floor(Math.random() * base + base/2),
        leads: Math.floor(Math.random() * (base/6))
    }));
};

export default Analytics;
